import { resetCardPointerState } from "./card-pointer-state";
import { gsap } from "./gsap";

/**
 * The window-from-card transition. An overlay window opens by growing out of
 * the card that opened it: the window starts as a sheet exactly over the
 * card, clipped to the card's size and moved onto the card's place, then the
 * clip widens to the whole window while the sheet travels to the window's
 * place and the contents rise in. Closing runs the other way and folds the
 * window back into the card. The move matters on a wide screen, where a card
 * can sit far outside the centred window. A card that is off screen, or no
 * card at all, falls back to a plain scale-in.
 */

const CARD_RADIUS = 13;
const WINDOW_RADIUS = 16;

export function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function visibleRect(rect: DOMRect | null | undefined): DOMRect | null {
  if (!rect || rect.width < 8 || rect.height < 8) return null;
  if (
    rect.bottom <= 0 ||
    rect.right <= 0 ||
    rect.top >= window.innerHeight ||
    rect.left >= window.innerWidth
  ) {
    return null;
  }
  return rect;
}

/**
 * The overlay windows are centred by their stylesheet with
 * `translate(-50%, -50%)`. GSAP reads that back as pixels and guesses the
 * -50% from the element's rounded offset size; at a window height with a
 * fraction (`88vh` on many screens) the guess fails, GSAP keeps the half
 * height as a fixed pixel offset, and setting `y` then drops the window by
 * half its height until the inline transform is cleared. Stating the
 * percentages outright takes the guess away.
 */
export function pinCentre(windowElement: HTMLElement): void {
  gsap.set(windowElement, { xPercent: -50, yPercent: -50 });
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

/** An `inset()` clip: the four insets and the corner radius, in pixels. */
interface Clip {
  top: number;
  right: number;
  bottom: number;
  left: number;
  radius: number;
}

const FULL_CLIP: Clip = { top: 0, right: 0, bottom: 0, left: 0, radius: WINDOW_RADIUS };

function clipText(clip: Clip): string {
  return `inset(${clip.top}px ${clip.right}px ${clip.bottom}px ${clip.left}px round ${clip.radius}px)`;
}

/**
 * Tweens the clip's numbers and writes the clip from them. Handing GSAP two
 * clip strings goes wrong: the browser reads an inline clip back in short
 * form (`inset(0px round 16px)`), GSAP pairs the two strings' numbers in
 * order, and with the counts unequal the radius grows from an inset and an
 * edge jumps, so the sheet's corner turned square and its edge slipped
 * outside the outline as the window folded into the card.
 */
function clipTween(
  windowElement: HTMLElement,
  from: Clip,
  to: Clip,
  vars: gsap.TweenVars
): gsap.core.Tween {
  const clip = { ...from };
  return gsap.to(clip, {
    ...to,
    ...vars,
    onUpdate: () => {
      windowElement.style.clipPath = clipText(clip);
    }
  });
}

/*
 * A window grows out of the card as the card stands at rest and in full
 * view. A card under the pointer leans towards it, and the flat sheet would
 * jump out of the leaning card, so the card first straightens up. A card
 * partly hidden under a pinned bar or past the list's edge is scrolled into
 * full view, or the sheet growing above everything would show the hidden
 * part all at once. Both happen together, before the window opens.
 */

/** How long a leaning card takes to straighten (its stylesheet's transition). */
const STRAIGHTEN_TIME = 0.28;
/** Room left between a card scrolled into view and the edge it came from. */
const SCROLL_MARGIN = 12;

function scrollerOf(element: Element): HTMLElement | null {
  for (let node = element.parentElement; node; node = node.parentElement) {
    if (
      /(auto|scroll|overlay)/.test(getComputedStyle(node).overflowY) &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
  }
  return null;
}

/**
 * The pinned (sticky or fixed) element covering the card at a point, if any:
 * one inside the scroller, or one laid over it from outside. The scroller's
 * own ancestors do not count, so a panel that is itself fixed does not cover
 * the cards it scrolls.
 */
function pinnedOver(card: Element, scroller: HTMLElement, x: number, y: number): Element | null {
  for (
    let node = document.elementFromPoint(x, y);
    node && node !== scroller;
    node = node.parentElement
  ) {
    if (card.contains(node)) return null;
    const { position } = getComputedStyle(node);
    if (position === "sticky" || position === "fixed") return node;
  }
  return null;
}

/** How far apart the points that look for a pinned bar over the card lie. */
const PROBE_STEP = 3;

interface Band {
  top: number;
  bottom: number;
  /** Whether a pinned bar, not the scroller's own edge, bounds the band. */
  pinnedTop: boolean;
  pinnedBottom: boolean;
}

/**
 * The band of the scroller where the card would show whole: the scroller's
 * own box, less any pinned bar over the card or just beyond its edges. The
 * card is probed down its whole height, not only at its edges: a pinned date
 * sits a few pixels below the scroller's top, behind a strip drawn by a
 * shadow that takes no pointer, so a card scrolled up under it shows through
 * that strip and the bar is found only further down. A bar in the upper half
 * of the scroller bounds the band from above and one in the lower half from
 * below, wherever on the card it was found: a card that only shows its
 * bottom edge has the whole bar across its upper part.
 */
function visibleBand(card: Element, scroller: HTMLElement): Band {
  const box = scroller.getBoundingClientRect();
  const band: Band = {
    top: Math.max(0, box.top + scroller.clientTop),
    bottom: Math.min(window.innerHeight, box.top + scroller.clientTop + scroller.clientHeight),
    pinnedTop: false,
    pinnedBottom: false
  };
  const middle = (band.top + band.bottom) / 2;
  const rect = card.getBoundingClientRect();
  const from = Math.max(rect.top - SCROLL_MARGIN, band.top);
  const to = Math.min(rect.bottom + SCROLL_MARGIN, band.bottom);
  const found = new Set<Element>();
  for (const x of [rect.left + 16, rect.left + rect.width / 2, rect.right - 16]) {
    for (let y = from + 1; y < to; y += PROBE_STEP) {
      const pinned = pinnedOver(card, scroller, x, y);
      if (pinned) found.add(pinned);
    }
  }
  for (const pinned of found) {
    const bar = pinned.getBoundingClientRect();
    if ((bar.top + bar.bottom) / 2 < middle) {
      band.top = Math.max(band.top, bar.bottom);
      band.pinnedTop = true;
    } else {
      band.bottom = Math.min(band.bottom, bar.top);
      band.pinnedBottom = true;
    }
  }
  return band;
}

/** Where the scroller must stand for the whole card to show, or null if it does. */
function scrollIntoBand(card: Element, scroller: HTMLElement): number | null {
  const band = visibleBand(card, scroller);
  const rect = card.getBoundingClientRect();
  // A pinned bar fades out a little past its own box, so a card is kept the
  // whole margin clear of one; the scroller's own edge cuts clean.
  const top = band.top + (band.pinnedTop ? SCROLL_MARGIN : 0);
  const bottom = band.bottom - (band.pinnedBottom ? SCROLL_MARGIN : 0);
  let delta = 0;
  if (rect.top < top - 0.5 || rect.height > band.bottom - band.top) {
    delta = rect.top - band.top - SCROLL_MARGIN;
  } else if (rect.bottom > bottom + 0.5) {
    delta = rect.bottom - band.bottom + SCROLL_MARGIN;
  }
  const target = clamp(
    scroller.scrollTop + delta,
    0,
    scroller.scrollHeight - scroller.clientHeight
  );
  return Math.abs(target - scroller.scrollTop) < 1 ? null : target;
}

/**
 * Bring `card` to rest in full view before a window grows out of it. The
 * `overlays` are the window's own layers already over the page; they are
 * left out while the card's cover is measured. Returns the settling, to
 * open the window when it completes, or null when the card is already
 * settled.
 */
export function settleCard(
  card: Element | null,
  overlays: HTMLElement[] = []
): gsap.core.Timeline | null {
  if (!(card instanceof HTMLElement) || prefersReducedMotion()) return null;
  const timeline = gsap.timeline();
  const tilt = card.querySelector<HTMLElement>(".model-card-tilt");
  if (tilt?.dataset.active === "true" || card.matches(":hover")) {
    resetCardPointerState(card);
    timeline.to({}, { duration: STRAIGHTEN_TIME }, 0);
  }
  const scroller = scrollerOf(card);
  if (scroller) {
    const visibility = overlays.map((overlay) => overlay.style.visibility);
    overlays.forEach((overlay) => (overlay.style.visibility = "hidden"));
    const target = scrollIntoBand(card, scroller);
    overlays.forEach((overlay, index) => (overlay.style.visibility = visibility[index]!));
    if (target !== null) {
      // The scroll is written from a stand-in, and only until it lands. A
      // window's animations are undone when it unmounts, and undoing a
      // scroll would put the list back where it was, under the card the
      // window has just folded into.
      const scroll = { top: scroller.scrollTop };
      let landed = false;
      timeline.to(
        scroll,
        {
          top: target,
          duration: Math.min(0.45, 0.24 + Math.abs(target - scroll.top) / 1500),
          ease: "power2.inOut",
          onUpdate: () => {
            if (!landed) scroller.scrollTop = scroll.top;
          },
          onComplete: () => {
            landed = true;
          }
        },
        0
      );
    }
  }
  if (timeline.duration() > 0) return timeline;
  timeline.kill();
  return null;
}

interface CardFrame {
  /** The clip that leaves a card-sized piece of the window. */
  clip: Clip;
  /** The offset that puts that piece exactly over the card. */
  x: number;
  y: number;
}

interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface CardPlacement {
  frame: CardFrame;
  /** The card's rectangle on screen, as the clipped window covers it. */
  card: Box;
  /** The window's own rectangle at rest. */
  window: Box;
}

/**
 * Where `windowElement`, resting in its own place, must be clipped and moved
 * to cover only `card`. The piece is taken from the window as near the card
 * as the window reaches, so a card inside the window needs no move at all.
 */
function placeOnCard(windowElement: HTMLElement, card: DOMRect): CardPlacement {
  gsap.set(windowElement, { x: 0, y: 0, scale: 1 });
  const box = windowElement.getBoundingClientRect();
  const width = Math.min(card.width, box.width);
  const height = Math.min(card.height, box.height);
  const left = clamp(card.left - box.left, 0, box.width - width);
  const top = clamp(card.top - box.top, 0, box.height - height);
  const right = box.width - left - width;
  const bottom = box.height - top - height;
  return {
    frame: {
      clip: { top, right, bottom, left, radius: CARD_RADIUS },
      x: card.left - (box.left + left),
      y: card.top - (box.top + top)
    },
    card: { left: card.left, top: card.top, width, height },
    window: { left: box.left, top: box.top, width: box.width, height: box.height }
  };
}

/**
 * How the sheet travels. Inside the window the clip only widens, and a sharp
 * exponential curve reads as a snap open. A long trip across a wide screen
 * needs a gentler curve and a little more time, or the sheet seems to jump.
 */
function motionFor(frame: CardFrame, base: number): { duration: number; ease: string } {
  const travel = Math.hypot(frame.x, frame.y);
  if (travel < 2) return { duration: base, ease: "expo.inOut" };
  return { duration: base + Math.min(0.24, travel / 3000), ease: "power3.inOut" };
}

function contentsOf(windowElement: HTMLElement): HTMLElement[] {
  return [...windowElement.children].filter(
    (child): child is HTMLElement => child instanceof HTMLElement
  );
}

/*
 * The clip cuts away the window's own border and drop shadow, so a bare
 * clipped window travels as a borderless white sheet and its shadow appears
 * all at once when the clip is lifted. An outline element rides on the
 * clip's edge instead: it starts with the card's border and soft shadow and
 * ends with the window's, its shadow spreading out as the sheet grows. The
 * window's own shadow is held off until the outline's has fully spread to
 * match it, then the two swap in the same frame.
 */
const CARD_BORDER = "rgb(229, 221, 207)";
const CARD_SHADOW = "rgba(72, 49, 31, 0.48) 0px 12px 28px -18px";
const NO_SHADOW = "rgba(72, 49, 31, 0) 0px 0px 0px 0px";

function windowLook(windowElement: HTMLElement): { border: string; shadow: string } {
  const style = getComputedStyle(windowElement);
  const shadow = style.boxShadow;
  return {
    border: style.borderTopColor,
    shadow: shadow && shadow !== "none" ? shadow : ""
  };
}

/** Each window's outline, so a new transition replaces its window's own. */
const outlines = new WeakMap<HTMLElement, HTMLElement>();

function createOutline(windowElement: HTMLElement): HTMLElement {
  outlines.get(windowElement)?.remove();
  const outline = document.createElement("div");
  outlines.set(windowElement, outline);
  outline.className = "card-outline";
  outline.dataset.cardOutline = "";
  outline.setAttribute("aria-hidden", "true");
  document.body.append(outline);
  return outline;
}

/*
 * A card and the window it opens can show the same things: the mark, the
 * name, the developer, a tag. Each such pair flies between the two places
 * instead of fading: the window's own element is moved and scaled onto the
 * card's, then travels back to rest as the sheet grows. It moves with the
 * sheet's curve and timing, so it stays inside the growing clip. The two
 * sides draw a pair a little differently (a name set large takes the
 * font's display cut), so a copy of the card's element travels with it,
 * laid exactly over it, and the two cross over midway, while they move
 * fastest: the trip starts with the card's own drawing and ends with the
 * window's, and no change shows at either end. What only the card shows is
 * copied over the sheet and fades out as the sheet leaves; what only the
 * window shows rises in once the sheet has opened. Closing runs the other
 * way.
 */

/** One element the card and the window both show. */
export interface SharedElement {
  /** The card's element. */
  from: Element;
  /** The window's element, which makes the trip. */
  to: HTMLElement;
  /**
   * How the two are matched: `text` by font size, the text's start and its
   * middle, `box` by the whole element. Text is matched by the text itself
   * because the two can sit in line boxes of different heights.
   */
  fit: "text" | "box";
}

export interface CardFlight {
  shared: SharedElement[];
  /** Card elements the window has no counterpart for. */
  leftovers: Element[];
}

/**
 * The trip between `card` and a window `header`: each pair of selectors,
 * card side first, names an element both show; a card element the header
 * lacks, and each card element `leftovers` names, is one only the card shows.
 */
export function cardFlight(
  card: Element | null,
  header: Element | null,
  pairs: Array<[string, string, SharedElement["fit"]]>,
  leftovers: string
): CardFlight | undefined {
  if (!card || !header) return undefined;
  const flight: CardFlight = { shared: [], leftovers: [...card.querySelectorAll(leftovers)] };
  for (const [fromSelector, toSelector, fit] of pairs) {
    const from = card.querySelector(fromSelector);
    const to = header.querySelector<HTMLElement>(toSelector);
    if (from && to) flight.shared.push({ from, to, fit });
    else if (from) flight.leftovers.push(from);
  }
  return flight;
}

interface Flight {
  target: HTMLElement;
  /** The card's element. */
  source: Element;
  /**
   * What puts the window's element over the card's. Text scaled to the
   * card's size is still not as wide as the card's: the two are set with
   * different letter spacing, and a name set large takes a different cut of
   * the font. So text also starts with the letter spacing that makes it the
   * card's width.
   */
  start: { x: number; y: number; scale: number; letterSpacing?: string };
  /** The window's own letter spacing, for text that starts with another. */
  restSpacing?: string;
  /**
   * For text the two sides draw differently, where the copy of the card's
   * element ends, from its start over the card's: the same trip the target
   * makes, run from the other end. Every value of both trips moves in step
   * with the same curve, so the copy stays over the target the whole way.
   * The copy ends with the letter spacing that makes it the window's width,
   * so the two lines keep one width while they cross over.
   */
  twin?: { x: number; y: number; scale: number; letterSpacing?: string };
  /** The card's own letter spacing, the copy's at the card. */
  cardSpacing?: string;
  /** How much larger than its layout the card's element shows: the copy's scale at the card. */
  cardScale: number;
  /** Text the two sides set apart, glyph for glyph: its copy swaps rather than blends. */
  apart?: boolean;
}

function letterSpacing(style: CSSStyleDeclaration): number {
  return parseFloat(style.letterSpacing) || 0;
}

function textRect(element: Element): DOMRect {
  const range = document.createRange();
  range.selectNodeContents(element);
  const rect = range.getBoundingClientRect();
  return rect.width && rect.height ? rect : element.getBoundingClientRect();
}

/**
 * How much larger than its own layout an element shows on screen. A member
 * card stands forward of the others in its panel, scaled up a little, and a
 * copy of its parts, laid out afresh outside it, has to be scaled to match.
 */
function screenScale(element: Element): number {
  if (typeof DOMMatrixReadOnly === "undefined") return 1;
  let scale = 1;
  for (let node: Element | null = element; node; node = node.parentElement) {
    const transform = getComputedStyle(node).transform;
    if (!transform || transform === "none") continue;
    const matrix = new DOMMatrixReadOnly(transform);
    scale *= Math.hypot(matrix.m11, matrix.m12, matrix.m13);
  }
  return scale;
}

/**
 * The lines a text is set in, one rect each, from the first. A name a card
 * cuts short still measures its whole width here.
 */
function textLines(element: Element): DOMRect[] {
  const range = document.createRange();
  range.selectNodeContents(element);
  const lines: DOMRect[] = [];
  for (const rect of range.getClientRects()) {
    if (!rect.width || !rect.height) continue;
    const line = lines.find((other) => Math.abs(other.top - rect.top) < rect.height / 2);
    if (!line) {
      lines.push(DOMRect.fromRect(rect));
      continue;
    }
    const left = Math.min(line.left, rect.left);
    const top = Math.min(line.top, rect.top);
    line.width = Math.max(line.right, rect.right) - left;
    line.height = Math.max(line.bottom, rect.bottom) - top;
    line.x = left;
    line.y = top;
  }
  return lines;
}

/** Whether a line of text is set whole: on one line, and not cut short. */
function setWhole(element: Element): boolean {
  const cut =
    element instanceof HTMLElement &&
    element.clientWidth > 0 &&
    element.scrollWidth > element.clientWidth + 1;
  return !cut && textLines(element).length === 1;
}

/** A text is placed by its first line, the line both sides begin alike. */
function fitRect(element: Element, fit: SharedElement["fit"]): DOMRect {
  if (fit !== "text") return element.getBoundingClientRect();
  return textLines(element)[0] ?? element.getBoundingClientRect();
}

/**
 * Where each shared element must start, measured with the window at rest and
 * less the window's own offset onto the card.
 */
function planFlights(shared: SharedElement[], frame: CardFrame): Flight[] {
  return shared.flatMap(({ from, to, fit }) => {
    const source = fitRect(from, fit);
    const target = fitRect(to, fit);
    if (!source.height || !target.height) return [];
    const box = to.getBoundingClientRect();
    const fromStyle = getComputedStyle(from);
    const toStyle = getComputedStyle(to);
    const lift = screenScale(from);
    const scale =
      fit === "text"
        ? (parseFloat(fromStyle.fontSize) * lift) / parseFloat(toStyle.fontSize)
        : source.height / target.height;
    if (!Number.isFinite(scale) || scale <= 0) return [];
    const start: Flight["start"] = {
      x: source.left - box.left - scale * (target.left - box.left) - frame.x,
      y:
        source.top +
        source.height / 2 -
        box.top -
        scale * (target.top + target.height / 2 - box.top) -
        frame.y,
      scale
    };
    const flight: Flight = { target: to, source: from, start, cardScale: lift };
    if (fit !== "text") return [flight];
    // Letter spacing follows every character, so a line's width moves by its
    // character count for each pixel of spacing. Both lines' spacing moves in
    // step with their scale, so lines of one width at both ends keep one
    // width the whole way. Lines that say different things (a card can
    // shorten a name the window spells out), or say it set differently (a
    // long name the card cuts off and the window wraps onto a second line),
    // are only scaled, from their first line.
    const sameText =
      from.textContent === to.textContent && setWhole(from) && setWhole(to);
    const restSpacing = letterSpacing(toStyle);
    const startSpacing = restSpacing + (source.width / scale - target.width) / characters(to);
    if (sameText && Math.abs(startSpacing - restSpacing) > 0.01) {
      start.letterSpacing = `${startSpacing}px`;
      flight.restSpacing = `${restSpacing}px`;
    }
    if (sameText && !drawnDifferently(fromStyle, toStyle)) return [flight];
    // The window carries a point of the target at rest to
    // frame + box + start + scale × (point − box) at the trip's start; the
    // copy ends where that carries the copy's own corner from.
    const anchor = from.getBoundingClientRect();
    const cardSpacing = letterSpacing(fromStyle);
    // The copy is laid out at the card's own size and scaled by `lift` to
    // the card's on screen, so its spacing counts in its own pixels.
    const endSpacing =
      cardSpacing + (scale * target.width - source.width) / (lift * characters(from));
    flight.apart = !sameText;
    flight.twin = {
      x: box.left + (anchor.left - frame.x - box.left - start.x) / scale - anchor.left,
      y: box.top + (anchor.top - frame.y - box.top - start.y) / scale - anchor.top,
      scale: lift / scale
    };
    if (sameText && Math.abs(endSpacing - cardSpacing) > 0.01) {
      flight.twin.letterSpacing = `${endSpacing}px`;
      flight.cardSpacing = `${cardSpacing}px`;
    }
    return [flight];
  });
}

/** Whether the card and the window set a line of text differently. */
function drawnDifferently(card: CSSStyleDeclaration, opened: CSSStyleDeclaration): boolean {
  return (
    (["fontSize", "fontFamily", "fontWeight", "fontStyle", "color", "textTransform"] as const).some(
      (property) => card[property] !== opened[property]
    ) || Math.abs(letterSpacing(card) - letterSpacing(opened)) > 0.01
  );
}

function characters(element: Element): number {
  return Math.max(1, [...(element.textContent ?? "")].length);
}

/**
 * GSAP rounds a pixel value it sets on a style to a whole pixel unless told
 * not to; letter spacing comes in fractions, and rounded it widened or
 * narrowed a travelling line by a pixel a character.
 */
const EXACT = { autoRound: false } as const;

/**
 * A line set wider by its starting letter spacing is kept on one line: its
 * box keeps the width it has at rest, and on a narrow screen the wider line
 * broke onto a second one under the card's copy.
 */
function unbroken(start: Flight["start"]): gsap.TweenVars {
  return start.letterSpacing ? { whiteSpace: "nowrap" } : {};
}

/**
 * The parts of the window that rise in and fall away: its sections whole,
 * except that a section holding a travelling element is split down to the
 * parts beside it, so the travelling element itself stays in view.
 */
function fadingParts(contents: HTMLElement[], travelling: HTMLElement[]): HTMLElement[] {
  const parts: HTMLElement[] = [];
  const visit = (element: HTMLElement) => {
    if (travelling.includes(element)) return;
    if (!travelling.some((target) => element.contains(target))) {
      parts.push(element);
      return;
    }
    for (const child of element.children) {
      if (child instanceof HTMLElement) visit(child);
    }
  };
  contents.forEach(visit);
  return parts;
}

/**
 * The window's sections that hold a travelling element are raised over the
 * rest, so an element crossing the window on its way passes over the
 * sections rising in below it rather than under them.
 */
function raiseSections(contents: HTMLElement[], travelling: HTMLElement[]): HTMLElement[] {
  const sections = contents.filter((section) =>
    travelling.some((target) => section.contains(target))
  );
  for (const section of sections) {
    const positioned = getComputedStyle(section).position !== "static";
    gsap.set(section, positioned ? { zIndex: 1 } : { position: "relative", zIndex: 1 });
  }
  return sections;
}

/**
 * A raised section's bottom rule would stay where it is while the section
 * below rises in or falls away, a line left standing in the sheet. For the
 * trip the rule is drawn by the section below instead, as a line along its
 * top edge that moves and fades with it; at rest the two lines are the same
 * pixels, so handing the rule back changes nothing on screen.
 */
function lendRules(raised: HTMLElement[], parts: HTMLElement[]): () => void {
  const lent: Array<[HTMLElement, HTMLElement]> = [];
  for (const section of raised) {
    const below = section.nextElementSibling;
    if (!(below instanceof HTMLElement) || !parts.includes(below)) continue;
    const style = getComputedStyle(section);
    const width = parseFloat(style.borderBottomWidth);
    if (!width || style.borderBottomStyle === "none") continue;
    const own = getComputedStyle(below).boxShadow;
    const rule = `0 -${width}px 0 0 ${style.borderBottomColor}`;
    gsap.set(section, { borderBottomColor: "transparent", backgroundClip: "padding-box" });
    gsap.set(below, { boxShadow: own && own !== "none" ? `${rule}, ${own}` : rule });
    lent.push([section, below]);
  }
  return () => {
    for (const [section, below] of lent) {
      gsap.set(section, { clearProps: "borderBottomColor,backgroundClip" });
      gsap.set(below, { clearProps: "boxShadow" });
    }
  };
}

/** A copy of a card's text, laid over the sheet at the same place. */
function ghostOf(element: Element): HTMLElement {
  const rect = textRect(element);
  const style = getComputedStyle(element);
  const lift = screenScale(element);
  const ghost = document.createElement("span");
  ghost.className = "card-ghost";
  ghost.setAttribute("aria-hidden", "true");
  ghost.textContent = element.textContent;
  Object.assign(ghost.style, {
    left: `${rect.left}px`,
    top: `${rect.top}px`,
    height: `${rect.height / lift}px`,
    color: style.color,
    fontFamily: style.fontFamily,
    fontSize: style.fontSize,
    fontStyle: style.fontStyle,
    fontWeight: style.fontWeight,
    letterSpacing: style.letterSpacing,
    lineHeight: `${rect.height / lift}px`,
    textTransform: style.textTransform
  });
  document.body.append(ghost);
  if (lift !== 1) gsap.set(ghost, { scale: lift, transformOrigin: "0 0" });
  return ghost;
}

/**
 * A copy of a card's element, laid over it. Each of its parts carries its
 * computed style inline, so the copy draws the same outside the card that
 * styled it. A line of text is set as one line box of the text's own
 * height, which puts its glyphs where the card's are. A card standing
 * forward shows larger than its layout, and so does its copy.
 */
function twinOf(element: Element): HTMLElement {
  const twin = element.cloneNode(true) as HTMLElement;
  const parts = [twin, ...twin.querySelectorAll<HTMLElement | SVGElement>("*")];
  [element, ...element.querySelectorAll("*")].forEach((source, index) => {
    const part = parts[index]!;
    const style = getComputedStyle(source);
    for (const property of style) {
      part.style.setProperty(property, style.getPropertyValue(property));
    }
    part.removeAttribute("class");
    part.removeAttribute("id");
    if (part instanceof HTMLImageElement) {
      part.loading = "eager";
      part.decoding = "sync";
    }
  });
  const rect = element.getBoundingClientRect();
  const lift = screenScale(element);
  twin.className = "card-ghost";
  twin.setAttribute("aria-hidden", "true");
  Object.assign(twin.style, {
    position: "fixed",
    inset: "auto",
    left: `${rect.left}px`,
    top: `${rect.top}px`,
    margin: "0",
    transform: "none",
    translate: "none",
    rotate: "none",
    scale: "none",
    transition: "none",
    animation: "none",
    zIndex: "63",
    pointerEvents: "none",
    willChange: "transform, opacity"
  });
  if (getComputedStyle(element).display === "inline") {
    Object.assign(twin.style, {
      display: "block",
      width: "auto",
      height: `${rect.height / lift}px`,
      lineHeight: `${rect.height / lift}px`,
      whiteSpace: "pre"
    });
  }
  document.body.append(twin);
  if (lift !== 1) gsap.set(twin, { scale: lift, transformOrigin: "0 0" });
  return twin;
}

/** Each window's copies, so a new transition replaces its window's own. */
const ghosts = new WeakMap<HTMLElement, HTMLElement[]>();

/** A travelling element the window draws differently, with its copy. */
interface Pair {
  flight: Flight & { twin: NonNullable<Flight["twin"]> };
  twin: HTMLElement;
}

function createGhosts(
  windowElement: HTMLElement,
  leftovers: Element[],
  flights: Flight[]
): { copies: HTMLElement[]; pairs: Pair[] } {
  ghosts.get(windowElement)?.forEach((ghost) => ghost.remove());
  // A line of text is copied as its text; anything else, a row of tags say,
  // whole.
  const copies = leftovers.map((leftover) =>
    getComputedStyle(leftover).display === "inline" ? ghostOf(leftover) : twinOf(leftover)
  );
  const pairs = flights.flatMap((flight) =>
    flight.twin ? [{ flight: { ...flight, twin: flight.twin }, twin: twinOf(flight.source) }] : []
  );
  ghosts.set(windowElement, [...copies, ...pairs.map(({ twin }) => twin)]);
  return { copies, pairs };
}

/*
 * The pairs cross over in the middle of the trip, from 30% to 70% of it,
 * where they move fastest. The one arriving comes in under the one leaving,
 * which goes once the other is whole, so the line never thins out between
 * the two. Two lines set apart, glyph for glyph, would show both sets of
 * glyphs side by side that way; the one leaving goes first instead, then
 * the one arriving comes in.
 */
const CROSSOVER_START = 0.3;
const CROSSOVER_LENGTH = 0.4;

function crossOver(
  timeline: gsap.core.Timeline,
  { flight: paired, twin }: Pair,
  arriving: HTMLElement,
  at: number,
  duration: number,
  vars: gsap.TweenVars = {}
): void {
  const leaving = arriving === twin ? paired.target : twin;
  const half = { duration: duration / 2, ease: "sine.inOut" };
  const [first, second] = paired.apart ? [duration / 2, 0] : [0, duration / 2];
  timeline
    .to(arriving, { autoAlpha: 1, ...half, ...vars }, at + first)
    .to(leaving, { autoAlpha: 0, ...half }, at + second);
}

/**
 * Grow `windowElement` out of `origin`, then raise its contents in. With a
 * `flight`, the elements the card and the window share travel across.
 */
export function expandFromCard(
  windowElement: HTMLElement,
  origin: DOMRect | null | undefined,
  flight?: CardFlight
): gsap.core.Timeline {
  const card = visibleRect(origin);
  const contents = contentsOf(windowElement);
  const timeline = gsap.timeline();
  pinCentre(windowElement);
  if (!card) {
    timeline.fromTo(
      windowElement,
      { autoAlpha: 0, scale: 0.985, y: 8 },
      { autoAlpha: 1, scale: 1, y: 0, duration: 0.26, ease: "power2.out" }
    );
    return timeline;
  }

  const look = windowLook(windowElement);
  const place = placeOnCard(windowElement, card);
  const motion = motionFor(place.frame, 0.56);
  const flights = flight ? planFlights(flight.shared, place.frame) : [];
  const travelling = flights.map(({ target }) => target);
  const parts = fadingParts(contents, travelling);
  const raised = raiseSections(contents, travelling);
  const returnRules = lendRules(raised, parts);
  const outline = createOutline(windowElement);
  const { copies, pairs } = createGhosts(windowElement, flight?.leftovers ?? [], flights);
  const twins = pairs.map(({ twin }) => twin);
  const cleanUp = () => {
    outline.remove();
    copies.forEach((copy) => copy.remove());
    twins.forEach((twin) => twin.remove());
  };
  // Set the starting sheet now, not on the timeline's first tick, so no
  // frame can paint the whole window before it shrinks onto the card.
  gsap.set(windowElement, {
    autoAlpha: 1,
    boxShadow: "none",
    clipPath: clipText(place.frame.clip),
    x: place.frame.x,
    y: place.frame.y
  });
  gsap.set(parts, { autoAlpha: 0, y: 22, filter: "blur(6px)" });
  for (const { target, start, twin } of flights) {
    gsap.set(target, {
      ...start,
      ...unbroken(start),
      transformOrigin: "0 0",
      ...(twin ? { autoAlpha: 0 } : {}),
      ...EXACT
    });
  }
  gsap.set(twins, { transformOrigin: "0 0" });
  gsap.set(outline, {
    ...place.card,
    borderRadius: CARD_RADIUS,
    borderColor: CARD_BORDER,
    boxShadow: look.shadow ? CARD_SHADOW : "none"
  });

  const bloom = motion.duration * 0.6 + 0.45;
  timeline
    .to(windowElement, { x: 0, y: 0, ...motion }, 0)
    .add(clipTween(windowElement, place.frame.clip, FULL_CLIP, motion), 0)
    .to(
      outline,
      {
        ...place.window,
        borderRadius: WINDOW_RADIUS,
        borderColor: look.border,
        ...motion,
        onInterrupt: cleanUp
      },
      0
    )
    .to(
      parts,
      {
        autoAlpha: 1,
        y: 0,
        filter: "blur(0px)",
        duration: 0.42,
        ease: "power3.out",
        stagger: Math.min(0.08, 0.24 / Math.max(1, parts.length)),
        clearProps: "opacity,visibility,transform,filter"
      },
      motion.duration * 0.54
    );
  for (const { target, restSpacing } of flights) {
    timeline.to(
      target,
      {
        x: 0,
        y: 0,
        scale: 1,
        ...(restSpacing ? { letterSpacing: restSpacing } : {}),
        ...motion,
        ...EXACT,
        clearProps: "transform,transformOrigin,letterSpacing,whiteSpace"
      },
      0
    );
  }
  for (const { flight: paired, twin } of pairs) {
    timeline.to(twin, { ...paired.twin, ...motion, ...EXACT }, 0);
  }
  for (const pair of pairs) {
    crossOver(
      timeline,
      pair,
      pair.flight.target,
      motion.duration * CROSSOVER_START,
      motion.duration * CROSSOVER_LENGTH,
      { clearProps: "opacity,visibility" }
    );
  }
  if (copies.length) {
    timeline.to(copies, { autoAlpha: 0, duration: 0.2, ease: "power1.out" }, 0);
  }
  if (look.shadow) {
    // The shadow starts to spread once the sheet is well on its way and
    // keeps easing outward a little after the window has opened.
    timeline.to(
      outline,
      {
        boxShadow: look.shadow,
        duration: bloom,
        ease: "power2.out",
        onInterrupt: cleanUp
      },
      motion.duration * 0.4
    );
  }
  timeline
    .set(windowElement, { clearProps: "clipPath,transform,boxShadow" })
    .set(raised, { clearProps: "position,zIndex" })
    .call(returnRules)
    .call(cleanUp);
  return timeline;
}

/**
 * Fold `windowElement` back into `origin`. With a `flight`, the elements the
 * card and the window share travel back onto the card's.
 */
export function collapseToCard(
  windowElement: HTMLElement,
  origin: DOMRect | null | undefined,
  flight?: CardFlight
): gsap.core.Timeline {
  const card = visibleRect(origin);
  const contents = contentsOf(windowElement);
  const timeline = gsap.timeline();
  pinCentre(windowElement);
  if (!card) {
    timeline.to(windowElement, {
      autoAlpha: 0,
      scale: 0.985,
      y: 8,
      duration: 0.18,
      ease: "power2.in"
    });
    return timeline;
  }

  const look = windowLook(windowElement);
  const place = placeOnCard(windowElement, card);
  const motion = motionFor(place.frame, 0.46);
  const flights = flight ? planFlights(flight.shared, place.frame) : [];
  const travelling = flights.map(({ target }) => target);
  const parts = fadingParts(contents, travelling);
  lendRules(raiseSections(contents, travelling), parts);
  const outline = createOutline(windowElement);
  const { copies, pairs } = createGhosts(windowElement, flight?.leftovers ?? [], flights);
  const twins = pairs.map(({ twin }) => twin);
  const cleanUp = () => {
    outline.remove();
    copies.forEach((copy) => copy.remove());
    twins.forEach((twin) => twin.remove());
  };
  gsap.set(windowElement, { clipPath: clipText(FULL_CLIP), boxShadow: "none" });
  for (const { target, start } of flights) {
    gsap.set(target, { transformOrigin: "0 0", ...unbroken(start) });
  }
  gsap.set(copies, { autoAlpha: 0 });
  for (const { flight: paired, twin } of pairs) {
    gsap.set(twin, { ...paired.twin, transformOrigin: "0 0", autoAlpha: 0, ...EXACT });
  }
  gsap.set(outline, {
    ...place.window,
    borderRadius: WINDOW_RADIUS,
    borderColor: look.border,
    boxShadow: look.shadow || "none"
  });
  timeline
    .to(parts, {
      autoAlpha: 0,
      y: 12,
      filter: "blur(4px)",
      duration: 0.18,
      ease: "power2.in"
    })
    .to(windowElement, { x: place.frame.x, y: place.frame.y, ...motion }, 0.06)
    .add(clipTween(windowElement, FULL_CLIP, place.frame.clip, motion), 0.06)
    .to(
      outline,
      {
        ...place.card,
        borderRadius: CARD_RADIUS,
        borderColor: CARD_BORDER,
        ...(look.shadow ? { boxShadow: NO_SHADOW } : {}),
        ...motion,
        onInterrupt: cleanUp
      },
      0.06
    );
  for (const { target, start } of flights) {
    timeline.to(target, { ...start, ...motion, ...EXACT }, 0.06);
  }
  for (const { flight: paired, twin } of pairs) {
    timeline.to(
      twin,
      {
        x: 0,
        y: 0,
        scale: paired.cardScale,
        ...(paired.cardSpacing ? { letterSpacing: paired.cardSpacing } : {}),
        ...motion,
        ...EXACT
      },
      0.06
    );
  }
  for (const pair of pairs) {
    crossOver(
      timeline,
      pair,
      pair.twin,
      0.06 + motion.duration * CROSSOVER_START,
      motion.duration * CROSSOVER_LENGTH
    );
  }
  if (copies.length) {
    // What only the card shows comes back as the shared elements land.
    timeline.to(
      copies,
      { autoAlpha: 1, duration: 0.12, ease: "power1.in" },
      0.06 + Math.max(0, motion.duration - 0.12)
    );
  }
  // A blank sheet thins out as it lands, so the card shows through it
  // instead of a blank card sitting there for a moment. A sheet carrying the
  // card's elements waits until they have landed: by then it looks just
  // like the card, while thinning out any sooner would show the card's
  // elements beside their copies still on their way. The copies then thin
  // out with the sheet over the card's own, which are drawn the same.
  timeline
    .to(
      flights.length ? [windowElement, outline, ...copies, ...twins] : [windowElement, outline],
      { autoAlpha: 0, duration: flights.length ? 0.12 : 0.18, ease: "power1.in" },
      flights.length ? 0.06 + motion.duration : "-=0.16"
    )
    .call(cleanUp);
  return timeline;
}

/**
 * Behind a window growing out of one card, the other cards of the same grid
 * step back and soften, rippling outward from the opened card; `restore`
 * brings them forward again in the same order.
 */
export function recedeCards(
  cards: HTMLElement[],
  active: HTMLElement | null
): gsap.core.Timeline {
  const index = active ? cards.indexOf(active) : 0;
  const others = cards.filter((card) => card !== active);
  const timeline = gsap.timeline();
  if (active) {
    timeline.to(active, { scale: 1.03, duration: 0.24, ease: "power2.out" }, 0);
  }
  timeline.to(
    others,
    {
      scale: 0.92,
      autoAlpha: 0.3,
      filter: "blur(2px) saturate(0.6)",
      duration: 0.42,
      ease: "power3.out",
      stagger: { each: 0.035, from: Math.max(0, index) }
    },
    0
  );
  return timeline;
}

/**
 * While the cards are stepped back, moving the window to another card's
 * detail hands the forward place to that card, so the window always folds
 * back into a card that stands out.
 */
export function shiftCardFocus(
  from: HTMLElement | null,
  to: HTMLElement
): gsap.core.Timeline {
  const timeline = gsap.timeline();
  if (from && from !== to) {
    timeline.to(
      from,
      {
        scale: 0.92,
        autoAlpha: 0.3,
        filter: "blur(2px) saturate(0.6)",
        duration: 0.32,
        ease: "power3.out"
      },
      0
    );
  }
  timeline.to(
    to,
    {
      scale: 1.03,
      autoAlpha: 1,
      filter: "blur(0px) saturate(1)",
      duration: 0.32,
      ease: "power3.out"
    },
    0
  );
  return timeline;
}

export function restoreCards(
  cards: HTMLElement[],
  active: HTMLElement | null
): gsap.core.Timeline {
  const index = active ? cards.indexOf(active) : 0;
  // A member dimmed by the current filters returns to its dimmed look, which
  // its stylesheet takes over once the inline values are cleared.
  const dimmed = (_index: number, card: HTMLElement) =>
    card.classList.contains("model-card-shell--dim");
  return gsap.timeline().to(cards, {
    scale: 1,
    autoAlpha: (index: number, card: HTMLElement) => (dimmed(index, card) ? 0.38 : 1),
    filter: (index: number, card: HTMLElement) =>
      dimmed(index, card) ? "blur(0px) saturate(0.6)" : "blur(0px) saturate(1)",
    duration: 0.4,
    ease: "back.out(1.6)",
    stagger: { each: 0.04, from: Math.max(0, index) },
    clearProps: "opacity,visibility,transform,filter"
  });
}
